import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { certificateUrl, ogImageUrl, pngDownloadUrl, shareTargets } from '../logic/certificate'
import type { LockDetail } from '../types'
import { Icon } from '../../TokenPages/icons'
import { CopyButton, Panel, PanelTitle, Stack } from '../ui'

const Buttons = styled.div<{ $hero?: boolean }>`
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  ${({ theme }) => theme.mediaQueries.sm} {
    grid-template-columns: repeat(${({ $hero }) => ($hero ? 4 : 3)}, minmax(0, 1fr));
  }
  ${({ theme }) => theme.mediaQueries.md} {
    grid-template-columns: repeat(${({ $hero }) => ($hero ? 4 : 6)}, minmax(0, 1fr));
  }
  a,
  button {
    width: 100%;
    justify-content: center;
  }
`
const Act = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 48px;
  padding: 0 10px;
  border-radius: 14px;
  font-size: 14px;
  svg {
    flex: none;
  }
  font-weight: 800;
  text-decoration: none;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.text};
  background: rgba(127, 212, 232, 0.1);
  border: 1px solid rgba(127, 212, 232, 0.4);
  &:hover {
    background: rgba(127, 212, 232, 0.2);
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`
const Preview = styled.div`
  position: relative;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  background: #060a1f;
  aspect-ratio: 1200 / 630;
  img {
    display: block;
    width: 100%;
    height: auto;
    aspect-ratio: 1200 / 630;
  }
  > span {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    text-align: center;
    font-size: 13px;
    color: #9fb4ec;
  }
`

/** The four targets the first viewport promises: post, send, copy, save. Used inside the certificate hero and again in the full panel. */
export function ShareActions({ lock, origin, now, hero }: { lock: LockDetail; origin: string; now: number; hero?: boolean }) {
  const url = certificateUrl(origin, lock)
  const targets = shareTargets(lock, origin, now)
  const list = hero ? targets.filter((t) => t.id === 'x' || t.id === 'telegram') : targets
  return (
    <Buttons $hero={hero} data-testid={hero ? 'share-hero' : 'share-all'}>
      {list.map((t) => (
        <Act key={t.id} href={t.href} target="_blank" rel="noopener noreferrer" data-testid={hero ? `hero-share-${t.id}` : `share-${t.id}`}>
          <Glyph name={t.id} /> {t.label}
        </Act>
      ))}
      <CopyButton text={url} label="Copy link" testid={hero ? 'hero-copy-link' : 'copy-link'} />
      <Act href={pngDownloadUrl(origin, lock)} download={`cadinu-lock-${lock.type}-${lock.id}.png`} data-testid={hero ? 'hero-download-png' : 'download-png'}>
        <Icon name="arrowDown" size={18} /> Download PNG
      </Act>
    </Buttons>
  )
}

const BRAND: Record<string, string> = {
  x: 'M5 4.5 19 19.5M19 4.5 5 19.5',
  telegram: 'M20.5 4 3.5 10.8l5.3 2 1.7 5.4 2.9-3.3 4.7 3.5L20.5 4zM8.8 12.8 20.5 4',
  facebook: 'M14 8.5V6.8c0-.9.6-1.3 1.4-1.3H17V3h-2.4C12.2 3 11 4.7 11 7v1.5H8.5V12H11v9h3v-9h2.6l.4-3.5H14z',
  linkedin: 'M6.5 9.5V20M6.5 4.5v.01M11 20v-6.2c0-2.3 1.5-3.8 3.5-3.8s3 1.5 3 3.8V20M11 9.5V20',
  whatsapp: 'M4 20l1.2-3.8A8 8 0 1 1 8 19.1L4 20zM9 8.5c0 3.3 3.2 6.5 6.5 6.5l1-1.5-2-1-1 .8a5 5 0 0 1-2.8-2.8l.8-1-1-2L9 8.5z',
  reddit: 'M12 9c3.9 0 7 2 7 4.7S15.9 18.5 12 18.5 5 16.4 5 13.7 8.1 9 12 9zM9.5 13v.01M14.5 13v.01M9.8 15.6c1.3.8 3.1.8 4.4 0M12 9l1.2-4.5 3.3.8M17.5 6.3a1 1 0 1 0 0-.01M18.5 10.5a1.5 1.5 0 1 1 1.2 2.4M5.5 10.5a1.5 1.5 0 1 0-1.2 2.4',
}
const Glyph = ({ name }: { name: string }) => (
  <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={BRAND[name]} />
  </svg>
)

/** Post it, send it, save it: X and Telegram open with the sentence and the link, the PNG is the same image the link preview shows, at twice the size. */
export default function SharePanel({ lock, origin, now }: { lock: LockDetail; origin: string; now: number }) {
  const [state, setState] = useState<'loading' | 'ok' | 'failed'>('loading')
  return (
    <Panel data-testid="share-panel">
      <Stack $gap={14}>
        <PanelTitle icon="upload">Share this certificate</PanelTitle>
        <Preview>
          {/* the image the link preview shows: rendered by the server from the chain data */}
          <img src={ogImageUrl(origin, lock)} alt={`Certificate of ${lock.title}, the card shown in link previews`} loading="eager" width={1200} height={630} data-testid="cert-preview" data-state={state} onLoad={() => setState('ok')} onError={() => setState('failed')} style={state === 'ok' ? undefined : { opacity: 0 }} />
          {state === 'loading' ? <span>Rendering the card from the chain…</span> : state === 'failed' ? <span>The preview could not be rendered just now. The link and Download PNG still work.</span> : null}
        </Preview>
        <ShareActions lock={lock} origin={origin} now={now} />
        <p style={{ margin: 0, fontSize: 13, lineHeight: '19px', color: '#9fb4ec' }}>Pasted anywhere (X, Telegram, Facebook, LinkedIn, Discord) the link unfolds into this card. The image carries a code and the link back here, and the page reads the lock from the chain, so the image cannot say more than the contract does.</p>
      </Stack>
    </Panel>
  )
}

const MenuWrap = styled.div<{ $inline?: boolean }>`
  position: relative;
  display: ${({ $inline }) => ($inline ? 'grid' : 'inline-flex')};
  gap: 10px;
  > button {
    justify-content: center;
  }
`
const ShareBtn = styled.button<{ $ghost?: boolean }>`
  all: unset;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 48px;
  padding: 0 22px;
  border-radius: 14px;
  cursor: pointer;
  font-weight: 800;
  font-size: 15px;
  color: #1b1203;
  background: linear-gradient(180deg, #ffe6a0 0%, #f2c75c 55%, #d49a2c 100%);
  box-shadow: 0 10px 26px -12px rgba(242, 199, 92, 0.9), inset 0 1px 0 rgba(255, 255, 255, 0.6);
  &:hover {
    filter: brightness(1.06);
  }
  /* the secondary twin beside a primary action (a live competition's "Buy ... to enter"): the same size, gold on the night instead of gold fill */
  ${({ $ghost }) =>
    $ghost
      ? `
    min-height: 52px;
    color: #ffd978;
    background: linear-gradient(180deg, rgba(255, 230, 160, 0.1), rgba(212, 154, 44, 0.04));
    box-shadow: 0 0 0 1px rgba(242, 199, 92, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.08);
    &:hover {
      filter: none;
      background: linear-gradient(180deg, rgba(255, 230, 160, 0.18), rgba(212, 154, 44, 0.08));
    }
  `
      : ''}
  &:focus-visible {
    outline: 2px solid #ffe6a0;
    outline-offset: 2px;
  }
`
const Pop = styled.div<{ $inline?: boolean }>`
  position: ${({ $inline }) => ($inline ? 'static' : 'absolute')};
  z-index: 30;
  top: calc(100% + 8px);
  left: 0;
  width: ${({ $inline }) => ($inline ? 'auto' : 'min(420px, calc(100vw - 32px))')};
  padding: 12px;
  border-radius: 18px;
  background: #0b1020;
  border: 1px solid rgba(242, 199, 92, 0.35);
  box-shadow: 0 24px 60px -18px rgba(0, 0, 0, 0.85);
  display: grid;
  gap: 10px;
`
const MenuGrid = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  a,
  button {
    width: 100%;
    justify-content: center;
  }
`

export interface ShareTarget {
  id: string
  label: string
  href: string
}

/**
 * One "Share" button: the card the link unfolds into, one button per network, Copy link and Download PNG. Shared by the lock
 * certificate and the trading competitions (Bez, 2026-10-03: the same set everywhere).
 */
export function SocialShareMenu({
  url,
  previewSrc,
  previewAlt,
  targets,
  downloadHref,
  downloadName,
  note,
  label = 'Share',
  dialogLabel = 'Share',
  align = 'left',
  buttonTestId = 'share-hero',
  inline = false,
  tone = 'gold',
}: {
  url: string
  previewSrc: string
  previewAlt: string
  targets: readonly ShareTarget[]
  downloadHref?: string
  downloadName?: string
  note?: string
  label?: string
  dialogLabel?: string
  align?: 'left' | 'right'
  buttonTestId?: string
  /** inside a dialog: the menu opens in the flow under the button instead of floating (a floating one is clipped by the dialog) */
  inline?: boolean
  /** 'ghost' when another button on the card is the primary action */
  tone?: 'gold' | 'ghost'
}) {
  const [open, setOpen] = useState(false)
  const [state, setState] = useState<'loading' | 'ok' | 'failed'>('loading')
  const ref = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!open) return undefined
    // a press outside closes the menu
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('touchstart', onDown)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('touchstart', onDown)
    }
  }, [open])
  const pop = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (open && inline) pop.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [open, inline])
  return (
    <MenuWrap
      ref={ref}
      $inline={inline}
      onKeyDown={(e) => {
        // the first Escape closes the menu only, not the dialog around it
        if (e.key === 'Escape' && open) {
          e.stopPropagation()
          setOpen(false)
        }
      }}
    >
      <ShareBtn type="button" $ghost={tone === 'ghost'} aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen((o) => !o)} data-testid={buttonTestId}>
        <Icon name="upload" size={18} /> {label}
      </ShareBtn>
      {open ? (
        <Pop ref={pop} $inline={inline} role="dialog" aria-label={dialogLabel} data-testid="share-menu" style={align === 'right' && !inline ? { left: 'auto', right: 0 } : undefined}>
          <Preview>
            <img src={previewSrc} alt={previewAlt} width={1200} height={630} data-testid="cert-preview" data-state={state} onLoad={() => setState('ok')} onError={() => setState('failed')} style={state === 'ok' ? undefined : { opacity: 0 }} />
            {state === 'loading' ? <span>Rendering the card…</span> : state === 'failed' ? <span>The preview could not be rendered just now. The link and Download PNG still work.</span> : null}
          </Preview>
          <MenuGrid data-testid="share-all">
            {targets.map((t) => (
              <Act key={t.id} href={t.href} target="_blank" rel="noopener noreferrer" data-testid={`share-${t.id}`}>
                <Glyph name={t.id} /> {t.label}
              </Act>
            ))}
            <CopyButton text={url} label="Copy link" testid="copy-link" />
            {downloadHref ? (
              <Act href={downloadHref} download={downloadName} data-testid="download-png">
                <Icon name="arrowDown" size={18} /> Download PNG
              </Act>
            ) : null}
          </MenuGrid>
          {note ? <p style={{ margin: 0, fontSize: 12, lineHeight: '17px', color: '#9fb4ec' }}>{note}</p> : null}
        </Pop>
      ) : null}
    </MenuWrap>
  )
}

/** One "Share" button (Bez, 2026-10-02): X, Telegram, Facebook, LinkedIn, Copy link and Download PNG, with the card the link unfolds into. */
export function ShareMenu({ lock, origin, now }: { lock: LockDetail; origin: string; now: number }) {
  return (
    <SocialShareMenu
      url={certificateUrl(origin, lock)}
      previewSrc={ogImageUrl(origin, lock)}
      previewAlt={`Certificate of ${lock.title}, the card shown in link previews`}
      targets={shareTargets(lock, origin, now)}
      downloadHref={pngDownloadUrl(origin, lock)}
      downloadName={`cadinu-lock-${lock.type}-${lock.id}.png`}
      dialogLabel="Share this certificate"
      note="Share the link, not a screenshot: it opens this lock read live from the chain, and the card updates with it."
    />
  )
}
